<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAdmin } from 'dashboard/composables/useAdmin';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  comment: { type: Object, required: true },
  isLoading: { type: Boolean, default: false },
});

const emit = defineEmits([
  'hide',
  'unhide',
  'delete',
  'like',
  'unlike',
  'handle',
]);

const { t } = useI18n();
const { isAdmin } = useAdmin();

const isHidden = computed(() => props.comment.status === 'hidden');

// A deleted comment no longer exists on Facebook, so hide/unhide, like/unlike, and
// delete would all fail against the Graph API (a handled 422, per
// SocialCommentsController's rescue_from Koala::Facebook::APIError -- not a crash,
// but a predictable failure the UI shouldn't let an agent walk into). Mark-handled
// is left out too: SocialComment.needs_reply is scoped to visible comments, so a
// deleted comment is already out of the queue regardless of handled_at -- there is
// nothing left for "mark handled" to do here.
const isDeleted = computed(() => props.comment.status === 'deleted');

const deleteDialogRef = ref(null);

const handleHideToggle = () => {
  if (isHidden.value) {
    emit('unhide');
  } else {
    emit('hide');
  }
};

const openDeleteDialog = () => {
  deleteDialogRef.value?.open();
};

const confirmDelete = () => {
  emit('delete');
  deleteDialogRef.value?.close();
};
</script>

<template>
  <div
    data-testid="comment-actions-bar"
    class="flex flex-wrap items-center gap-2 p-4 border-b border-n-weak"
  >
    <p
      v-if="isDeleted"
      data-testid="deleted-comment-notice"
      class="text-sm text-n-slate-10"
    >
      {{ t('COMMENTS.ACTIONS.DELETED_NOTICE') }}
    </p>
    <template v-else>
      <button
        type="button"
        data-testid="hide-toggle-button"
        :disabled="isLoading"
        class="h-8 px-3 text-sm font-medium border rounded-lg border-n-weak text-n-slate-12 disabled:opacity-50"
        @click="handleHideToggle"
      >
        {{
          isHidden ? t('COMMENTS.ACTIONS.UNHIDE') : t('COMMENTS.ACTIONS.HIDE')
        }}
      </button>
      <button
        type="button"
        data-testid="like-button"
        :disabled="isLoading"
        class="h-8 px-3 text-sm font-medium border rounded-lg border-n-weak text-n-slate-12 disabled:opacity-50"
        @click="emit('like')"
      >
        {{ t('COMMENTS.ACTIONS.LIKE') }}
      </button>
      <button
        type="button"
        data-testid="unlike-button"
        :disabled="isLoading"
        class="h-8 px-3 text-sm font-medium border rounded-lg border-n-weak text-n-slate-12 disabled:opacity-50"
        @click="emit('unlike')"
      >
        {{ t('COMMENTS.ACTIONS.UNLIKE') }}
      </button>
      <button
        type="button"
        data-testid="handle-button"
        :disabled="isLoading"
        class="h-8 px-3 text-sm font-medium border rounded-lg border-n-weak text-n-slate-12 disabled:opacity-50"
        @click="emit('handle')"
      >
        {{ t('COMMENTS.ACTIONS.MARK_HANDLED') }}
      </button>
      <button
        v-if="isAdmin"
        type="button"
        data-testid="delete-button"
        :disabled="isLoading"
        class="h-8 px-3 text-sm font-medium text-white rounded-lg bg-n-ruby-9 disabled:opacity-50"
        @click="openDeleteDialog"
      >
        {{ t('COMMENTS.ACTIONS.DELETE') }}
      </button>
    </template>

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="t('COMMENTS.ACTIONS.DELETE_DIALOG.TITLE')"
      :description="t('COMMENTS.ACTIONS.DELETE_DIALOG.DESCRIPTION')"
      :confirm-button-label="t('COMMENTS.ACTIONS.DELETE_DIALOG.CONFIRM')"
      @confirm="confirmDelete"
    />
  </div>
</template>
